import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dealersApi } from '../../../api/dealers.api';
import { evcpdProductsApi } from '../../../api/evcpdProducts.api';
import { installationsApi } from '../../../api/installations.api';
import type { AmcContract, CreateContractInput } from '../../../api/evcpdAmc.api';

interface Props {
  open: boolean;
  initial?: AmcContract | null;
  onClose: () => void;
  onSubmit: (input: CreateContractInput) => Promise<void>;
  error?: string | null;
}

const TYPES = [
  { value: 'comprehensive', label: 'Comprehensive' },
  { value: 'non_comprehensive', label: 'Non-Comprehensive' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'extended_warranty', label: 'Extended Warranty' },
];
const FREQ = ['monthly', 'quarterly', 'half_yearly', 'yearly', 'custom'];

const empty: CreateContractInput = { amcStartDate: '', amcEndDate: '', contractType: 'comprehensive', pmFrequency: 'quarterly', contractValue: 0 };

export function AmcContractFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateContractInput>(empty);

  const dealers = useQuery({ queryKey: ['dealers', 'amc-picker'], queryFn: () => dealersApi.list({ status: 'approved', limit: 100 }), enabled: open });
  const products = useQuery({ queryKey: ['evcpd-products', 'amc-picker'], queryFn: () => evcpdProductsApi.list({ status: 'active', limit: 200 }), enabled: open });
  const installs = useQuery({ queryKey: ['installations', 'amc-picker'], queryFn: () => installationsApi.list({ limit: 100 }), enabled: open });

  useEffect(() => {
    setForm(initial
      ? { amcStartDate: initial.amcStartDate ?? '', amcEndDate: initial.amcEndDate ?? '', contractType: initial.contractType, pmFrequency: initial.pmFrequency, contractValue: initial.contractValue, oem: initial.oem ?? undefined, cpo: initial.cpo ?? undefined, serialNumber: initial.serialNumber ?? undefined, clientId: initial.clientId, dealerId: initial.dealerId, productId: initial.productId, installationId: initial.installationId, pmIntervalDays: initial.pmIntervalDays }
      : empty);
  }, [initial, open]);

  const set = <K extends keyof CreateContractInput>(k: K, v: CreateContractInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? `Edit AMC — ${initial?.amcCode}` : 'New AMC Contract'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField select label="Dealer" value={form.dealerId ?? ''} onChange={(e) => set('dealerId', e.target.value ? Number(e.target.value) : null)} fullWidth disabled={isEdit}>
              <MenuItem value="">—</MenuItem>
              {(dealers.data?.data ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName}</MenuItem>)}
            </TextField>
            <TextField select label="Installation" value={form.installationId ?? ''} onChange={(e) => set('installationId', e.target.value ? Number(e.target.value) : null)} fullWidth disabled={isEdit}>
              <MenuItem value="">—</MenuItem>
              {(installs.data?.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.requestCode}</MenuItem>)}
            </TextField>
            <TextField select label="Product" value={form.productId ?? ''} onChange={(e) => set('productId', e.target.value ? Number(e.target.value) : null)} fullWidth disabled={isEdit}>
              <MenuItem value="">—</MenuItem>
              {(products.data?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Serial Number" value={form.serialNumber ?? ''} onChange={(e) => set('serialNumber', e.target.value)} fullWidth />
            <TextField label="OEM" value={form.oem ?? ''} onChange={(e) => set('oem', e.target.value)} fullWidth />
            <TextField label="CPO" value={form.cpo ?? ''} onChange={(e) => set('cpo', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Warranty Start" type="date" InputLabelProps={{ shrink: true }} value={form.warrantyStartDate ?? ''} onChange={(e) => set('warrantyStartDate', e.target.value)} fullWidth />
            <TextField label="Warranty End" type="date" InputLabelProps={{ shrink: true }} value={form.warrantyEndDate ?? ''} onChange={(e) => set('warrantyEndDate', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="AMC Start" type="date" InputLabelProps={{ shrink: true }} value={form.amcStartDate} onChange={(e) => set('amcStartDate', e.target.value)} required fullWidth />
            <TextField label="AMC End" type="date" InputLabelProps={{ shrink: true }} value={form.amcEndDate} onChange={(e) => set('amcEndDate', e.target.value)} required fullWidth />
            <TextField label="Contract Value (₹)" type="number" value={form.contractValue ?? ''} onChange={(e) => set('contractValue', Number(e.target.value))} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Contract Type" value={form.contractType ?? 'comprehensive'} onChange={(e) => set('contractType', e.target.value)} fullWidth>
              {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField select label="PM Frequency" value={form.pmFrequency ?? 'quarterly'} onChange={(e) => set('pmFrequency', e.target.value)} fullWidth>
              {FREQ.map((f) => <MenuItem key={f} value={f}>{f.replace(/_/g, ' ')}</MenuItem>)}
            </TextField>
            {form.pmFrequency === 'custom' && <TextField label="Interval (days)" type="number" value={form.pmIntervalDays ?? ''} onChange={(e) => set('pmIntervalDays', e.target.value ? Number(e.target.value) : null)} fullWidth />}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.amcStartDate || !form.amcEndDate} onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
